/* LesTresorsDeKanto — product.js
   Galerie (vignettes + zoom), sélecteur de variantes (prix, stock, URL,
   bouton d'achat) sans rechargement de page. */

(() => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slides = this.querySelectorAll('.product-gallery__slide');
      this.thumbs = this.querySelectorAll('[data-media-target]');

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(thumb.dataset.mediaTarget));
      });

      this.querySelectorAll('.product-gallery__image').forEach((image) => {
        image.addEventListener('click', () => image.classList.toggle('is-zoomed'));
      });

      // Swipe mobile
      let startX = null;
      const main = this.querySelector('.product-gallery__main');
      if (main) {
        main.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
        main.addEventListener(
          'touchend',
          (e) => {
            if (startX === null) return;
            const delta = e.changedTouches[0].clientX - startX;
            if (Math.abs(delta) > 45) this.step(delta < 0 ? 1 : -1);
            startX = null;
          },
          { passive: true }
        );
      }
    }

    activeIndex() {
      return [...this.slides].findIndex((s) => s.classList.contains('is-active'));
    }

    step(direction) {
      if (this.slides.length < 2) return;
      const next = (this.activeIndex() + direction + this.slides.length) % this.slides.length;
      this.show(this.slides[next].dataset.mediaId);
    }

    show(mediaId) {
      this.slides.forEach((slide) => {
        slide.classList.toggle('is-active', slide.dataset.mediaId === String(mediaId));
      });
      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaTarget === String(mediaId));
      });
    }
  }
  if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', MediaGallery);
  }

  /* ------------------------------------------------------------------ */
  /* Variant selector                                                    */
  /* ------------------------------------------------------------------ */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const dataScript = this.querySelector('script[type="application/json"]');
      if (!dataScript) return;
      this.variants = JSON.parse(dataScript.textContent);
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.selects = this.querySelectorAll('select');
      this.selects.forEach((select) => select.addEventListener('change', () => this.onChange()));
    }

    currentOptions() {
      return [...this.selects].map((select) => select.value);
    }

    matchedVariant() {
      const options = this.currentOptions();
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === options[index])
      );
    }

    async onChange() {
      const variant = this.matchedVariant();
      const productInfo = this.closest('product-info');
      const form = document.querySelector('#product-form-main');
      const idInput = form && form.querySelector('input[name="id"]');
      const addButton = form && form.querySelector('[name="add"]');
      const addLabel = addButton && addButton.querySelector('[data-add-label]');

      if (!variant) {
        if (addButton) {
          addButton.setAttribute('disabled', 'true');
          if (addLabel) addLabel.textContent = window.variantStrings.unavailable;
        }
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.disabled = !variant.available;
      }
      if (addButton) {
        addButton.toggleAttribute('disabled', !variant.available);
        if (addLabel) {
          addLabel.textContent = variant.available
            ? window.variantStrings.addToCart
            : window.variantStrings.soldOut;
        }
      }

      if (variant.featured_media && productInfo) {
        const gallery = productInfo.querySelector('media-gallery');
        if (gallery) gallery.show(variant.featured_media.id);
      }

      // URL + re-render du prix et du stock via la Section Rendering API.
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);

      try {
        const response = await fetch(
          `${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`
        );
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        ['price-' + this.sectionId, 'stock-' + this.sectionId].forEach((id) => {
          const fresh = parsed.getElementById(id);
          const current = document.getElementById(id);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
      } catch (e) {
        /* le prix affiché reste celui de la variante précédente jusqu'au prochain event */
      }
    }
  }
  if (!customElements.get('variant-selects')) {
    customElements.define('variant-selects', VariantSelects);
  }

  /* ------------------------------------------------------------------ */
  /* Product recommendations                                             */
  /* ------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fresh = parsed.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim().length) {
          this.innerHTML = fresh.innerHTML;
        }
        this.dataset.loaded = 'true';
      } catch (e) {
        /* pas de recommandations : la section reste vide */
      }
    }
  }
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }
})();
